import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams, useSearchParams } from 'react-router-dom';
import { useJobStore } from '@core/job/useJobStore';
import { useClientStore } from '@core/client/useClientStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import useFeatureView from '@/hooks/useFeatureView';
import { useJobDetailData } from '@/modules/jobs/hooks/useJobDetailData';
import {
  getJobDetailView,
  type JobDetailTabKey,
} from '@/modules/jobs/views/jobDetail.view';
import Breadcrumbs from '@/components/Breadcrumbs';
import JobEditModal from '@/components/modals/JobEditModal';
import Tabs from '@/components/Tabs';
import {
  JobHeader,
  JobDetails,
  JobActivity,
  JobTimesheets,
  JobApplications,
} from '@/components/job';
import RateSection from '@/components/job/jobDrawer/rateSection/RateSection';

const ALL_JOB_TABS: { key: JobDetailTabKey; label: string }[] = [
  { key: 'details', label: 'Details' },
  { key: 'activity', label: 'Activity' },
  { key: 'applications', label: 'Applications' },
  { key: 'timesheets', label: 'Timesheets' },
];

const JobPage = () => {
  const { id } = useParams<{ id: string }>();
  const view = useFeatureView(getJobDetailView);
  const { assignWorkerToJob, fetchJobById } = useJobStore();
  const { workers } = useWorkerStore();
  const { getClientById } = useClientStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const { job, setJob, isLoading, error, availableWorkers, refreshJob } =
    useJobDetailData(view, id);

  const visibleTabs = useMemo(
    () => ALL_JOB_TABS.filter((tab) => view.tabs.includes(tab.key)),
    [view.tabs],
  );

  const [selectedTab, setSelectedTab] = useState<JobDetailTabKey>(() => {
    const tabParam = searchParams.get('tab') as JobDetailTabKey | null;
    if (tabParam && view.tabs.includes(tabParam)) {
      return tabParam;
    }
    return view.tabs[0] ?? 'details';
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-600">{error || 'Job not found'}</div>
      </div>
    );
  }

  const client = getClientById(job.clientId);

  const handleAssignWorker = async () => {
    if (!selectedWorkerId || !job || !view.actions.assignWorker) {
      return;
    }

    setIsAssigning(true);
    try {
      await assignWorkerToJob(job.id, selectedWorkerId);
      const updatedJob = await fetchJobById(job.id);
      if (updatedJob) {
        setJob(updatedJob);
        setSelectedWorkerId('');
        const selectedWorker = workers.find((w) => w.id === selectedWorkerId);
        const workerName = selectedWorker
          ? `${selectedWorker.firstName} ${selectedWorker.lastName}`
          : 'Worker';
        toast.success(`${workerName} successfully assigned to job!`, {
          position: 'bottom-left',
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(`Failed to assign worker to job: ${(err as Error).message}`, {
        position: 'bottom-left',
        autoClose: 3000,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="p-4">
      <Breadcrumbs
        items={[
          { label: 'Jobs', to: '/jobs' },
          { label: job.title || 'Job Details' },
        ]}
        className="mb-4"
      />
      <div className="mb-4">
        <JobHeader
          title={job.title || 'Job'}
          status={job.itemStatus || undefined}
          clientName={
            view.actions.showClientName ? (client?.name ?? '') : undefined
          }
          clientId={view.actions.showClientName ? job.clientId : undefined}
          clientLogoUrl={
            view.actions.showClientName ? (client?.logoUrl ?? '') : undefined
          }
        />
      </div>
      <Tabs
        tabs={visibleTabs}
        selectedTabKey={selectedTab}
        onTabChange={(key) => {
          setSelectedTab(key as JobDetailTabKey);
          setSearchParams({});
        }}
      >
        {selectedTab === 'details' && (
          <div className="space-y-6">
            <JobDetails
              job={job}
              availableWorkersForJob={
                view.actions.assignWorker ? availableWorkers : []
              }
              selectedWorkerId={selectedWorkerId}
              onWorkerSelect={setSelectedWorkerId}
              onAssignWorker={handleAssignWorker}
              isAssigning={isAssigning}
              onEdit={() => setIsEditModalOpen(true)}
              onCancelSuccess={() => {
                void refreshJob();
              }}
              showWorkerLink={view.actions.showWorkerLink}
              canAssignWorker={view.actions.assignWorker}
            />
            {view.actions.showRateSection && (
              <RateSection job={job} layout="page" onJobUpdated={setJob} />
            )}
          </div>
        )}
        {selectedTab === 'activity' && (
          <JobActivity
            job={job}
            eligibleCandidateCount={availableWorkers.length}
            isLoadingEligibleCandidates={isLoading}
          />
        )}
        {selectedTab === 'applications' && <JobApplications jobId={job.id} />}
        {selectedTab === 'timesheets' && <JobTimesheets job={job} />}
      </Tabs>
      {view.actions.editJob && (
        <JobEditModal
          isModalOpen={isEditModalOpen}
          setIsModalOpen={setIsEditModalOpen}
          job={job}
          onSuccess={() => {
            void refreshJob();
          }}
        />
      )}
    </div>
  );
};

export default JobPage;
